import React from "react";
import styles from "../../css/HavenExtraTable.module.css";
import { gradeToConvert100 } from "../../../../../util/specialCase";
import useFlexibleData from "../../../../../hooks/useFlexibleData";
import { useAuthContext } from "../../../../../context/AuthContext";
import { useUIContext } from "../../../../../context/UIContext";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

function HavenExtraTable({
  lessonStudentsData,
  setLessonStudentsData,
  lessonSetting,
  updateLessonStudents,
  lessonInfo,
  evalItemsData,
}) {
  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: lessonSetting[0]?.schoolid,
    main_category: "convert100",
  });
  const { t } = useTranslation();

  const { user, hasPermission } = useAuthContext();
  const { setIsLoadingModal, handleToastCenterTop, setAlertModalState } =
    useUIContext();

  // 빈 값 체크 헬퍼 함수
  const isEmptyGrade = (gradeValue) => {
    return gradeValue === "" || gradeValue === null || gradeValue === undefined;
  };

  const roundToTwo = (value) => Math.round(value * 100) / 100;

  function calculateLevel1Score(student, level1Item, evalItemsData) {
    if (!student || !level1Item || !evalItemsData) {
      console.warn("calculateLevel1Score: 필수 매개변수가 누락되었습니다.");
      return 0;
    }

    const covert100Setting = flexibleData?.find(
      (r) => r.sub_category === "평가항목별" && r.main_category === "convert100"
    )?.data_value;
    const excludeEmptyGrade = covert100Setting?.excludeEmptyGrade || false;

    const level1Weight = Number(level1Item.extra_data?.weight);
    if (!level1Weight || isNaN(level1Weight)) {
      console.warn(
        `Level1 항목 ${level1Item.id}의 가중치가 유효하지 않습니다.`
      );
      return "";
    }

    const level2Items = evalItemsData.filter(
      (item) => item.hierarchy_level === 2 && item.parent_id === level1Item.id
    );

    if (level2Items.length === 0) {
      const directScore = student.eval_item_grades?.[level1Item.id];
      const directTotal = level1Item.extra_data?.total;

      if (excludeEmptyGrade && isEmptyGrade(directScore)) {
        return "";
      }

      if (!directScore || !directTotal) {
        return "";
      }

      const percentage = (Number(directScore) / Number(directTotal)) * 100;
      return roundToTwo((percentage * level1Weight) / 100);
    }

    const hasLevel2Weights = level2Items.some((item) => {
      const weight = Number(item.extra_data?.weight || 0);
      return !isNaN(weight) && weight > 0;
    });

    const getLevel3Items = (level2Item) =>
      evalItemsData.filter(
        (item) =>
          item.hierarchy_level === 3 && item.parent_id === level2Item.id
      );

    const hasAnyFilledDescendant = level2Items.some((level2Item) => {
      const level3Items = getLevel3Items(level2Item);
      if (level3Items.length > 0) {
        return level3Items.some(
          (item) => !isEmptyGrade(student.eval_item_grades?.[item.id])
        );
      }
      return !isEmptyGrade(student.eval_item_grades?.[level2Item.id]);
    });

    if (excludeEmptyGrade && !hasAnyFilledDescendant) {
      return "";
    }

    if (!hasLevel2Weights) {
      let totalScore = 0;
      let totalPossible = 0;

      level2Items.forEach((level2Item) => {
        const level3Items = getLevel3Items(level2Item);

        if (level3Items.length > 0) {
          level3Items.forEach((level3Item) => {
            const score = student.eval_item_grades?.[level3Item.id];
            const total = Number(level3Item.extra_data?.total || 100);

            if (excludeEmptyGrade && isEmptyGrade(score)) return;
            if (isNaN(total)) return;

            totalPossible += total;
            if (score && score !== "" && !isNaN(score)) {
              totalScore += Number(score);
            }
          });
          return;
        }

        const score = student.eval_item_grades?.[level2Item.id];
        const total = level2Item.extra_data?.total;

        if (excludeEmptyGrade && isEmptyGrade(score)) return;
        if (!total || isNaN(total)) return;

        totalPossible += Number(total);
        if (score && score !== "" && !isNaN(score)) {
          totalScore += Number(score);
        }
      });

      const percentage = totalPossible ? (totalScore / totalPossible) * 100 : 0;
      return roundToTwo((percentage * level1Weight) / 100);
    }

    let level1WeightedScore = 0;

    level2Items.forEach((level2Item) => {
      const level2Weight = Number(level2Item.extra_data?.weight || 0);
      const level3Items = getLevel3Items(level2Item);

      if (level3Items.length === 0) {
        const score = student.eval_item_grades?.[level2Item.id];
        const total = Number(level2Item.extra_data?.total || 100);

        if (excludeEmptyGrade && isEmptyGrade(score)) return;
        if (!score || score === "" || isNaN(score) || !total) return;

        const percentage = (Number(score) / Number(total)) * 100;
        level1WeightedScore += (percentage * level2Weight) / 100;
        return;
      }

      const hasLevel3Weights = level3Items.some((item) => {
        const weight = Number(item.extra_data?.weight || 0);
        return !isNaN(weight) && weight > 0;
      });

      const scoredLevel3Items = excludeEmptyGrade
        ? level3Items.filter(
            (item) => !isEmptyGrade(student.eval_item_grades?.[item.id])
          )
        : level3Items;

      if (excludeEmptyGrade && scoredLevel3Items.length === 0) return;

      let level2WeightedScore = 0;

      if (hasLevel3Weights) {
        scoredLevel3Items.forEach((item) => {
          const score = student.eval_item_grades?.[item.id];
          if (!score || score === "" || isNaN(score)) return;

          const total = Number(item.extra_data?.total || 100);
          const weight = Number(item.extra_data?.weight || 0);
          const percentage = total > 0 ? (Number(score) / total) * 100 : 0;
          level2WeightedScore += (percentage * weight) / 100;
        });
      } else {
        let totalScore = 0;
        let totalPossible = 0;

        scoredLevel3Items.forEach((item) => {
          const score = student.eval_item_grades?.[item.id];
          const total = Number(item.extra_data?.total || 100);
          if (isNaN(total)) return;

          totalPossible += total;
          if (score && score !== "" && !isNaN(score)) {
            totalScore += Number(score);
          }
        });

        if (totalPossible > 0) {
          level2WeightedScore = (totalScore / totalPossible) * 100;
        }
      }

      level1WeightedScore += (level2WeightedScore * level2Weight) / 100;
    });

    return roundToTwo((level1WeightedScore * level1Weight) / 100);
  }

  const calculateDeductionTotalScore = (
    student,
    level1Items,
    evalItemsData,
    excludeEmptyGrade
  ) => {
    let totalDeduction = 0;

    level1Items.forEach((level1Item) => {
      const level1Weight = Number(level1Item.extra_data?.weight || 0);
      const maxPoints = level1Weight;

      const level2Items = evalItemsData.filter(
        (item) => item.hierarchy_level === 2 && item.parent_id === level1Item.id
      );

      if (level2Items.length === 0) {
        const score = student.eval_item_grades?.[level1Item.id];
        const total = level1Item.extra_data?.total;

        if (excludeEmptyGrade && isEmptyGrade(score)) return;

        if (score && total) {
          const earnedPoints = (Number(score) / Number(total)) * maxPoints;
          totalDeduction += maxPoints - earnedPoints;
        } else if (!excludeEmptyGrade && total) {
          totalDeduction += maxPoints;
        }
      } else {
        const earned = calculateLevel1Score(
          student,
          level1Item,
          evalItemsData
        );
        if (excludeEmptyGrade && earned === "") return;
        totalDeduction += maxPoints - (Number(earned) || 0);
      }
    });

    return Number((100 - totalDeduction).toFixed(2));
  };

  const saveStudentExtraData = (student, value) => {
    const { id, extra_data } = student;

    const updatedExtraData = {
      ...extra_data,
      academicStatus: value === "--------" ? null : value,
    };

    const dataSet = {
      id,
      extra_data: updatedExtraData,
    };

    const checkOwner = lessonInfo.teachers.some(
      (teacher) => teacher.username === user.username
    );
    if (!checkOwner && !hasPermission(null, "can_update")) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    setIsLoadingModal(true);
    updateLessonStudents.mutate(dataSet, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setLessonStudentsData(
          lessonStudentsData.map((student) => {
            if (student.id === id) {
              return { ...student, extra_data: updatedExtraData };
            }
            return student;
          })
        );

        setIsLoadingModal(false);
        setAlertModalState({
          visible: true,
          message: t("teacher.lessonRelated.savedWellMessage"),
        });
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  /*아렇게 간단히 할 수 있는 것을 저 select 때문에 아래와 같이 해야 하다니... 
  const handleExcelDownload = () => {
    console.log("hello");

    const table = document.querySelector(`.${styles.gradeTable}`);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(table);
    XLSX.utils.book_append_sheet(wb, ws, "Grades");
    XLSX.writeFile(wb, `${lessonInfo.subject_name}_grades.xlsx`);
  };
*/

  const handleExcelDownload = () => {
    // havenStyle 설정 확인
    const covert100Setting = flexibleData?.find(
      (r) => r.sub_category === "평가항목별" && r.main_category === "convert100"
    )?.data_value;
    const havenStyle = covert100Setting?.havenStyle || false;
    const excludeEmptyGrade = covert100Setting?.excludeEmptyGrade || false;

    // 엑셀 데이터 구성
    const excelData = lessonStudentsData.map((student) => {
      const row = {
        "Student Number": student.username,
        Name: student.fullname,
      };

      if (lessonInfo.evaluation_type === "평가항목별") {
        // 평가항목별인 경우 기존 로직
        // level1Items의 점수 추가
        level1Items.forEach((item) => {
          const score = calculateLevel1Score(student, item, evalItemsData);
          row[`${item.content} (${item?.extra_data?.weight}%)`] = score || "";
        });

        // 총점 계산 - havenStyle에 따라 다르게 계산
        let totalScore;
        if (havenStyle) {
          // 감점 방식
          totalScore = calculateDeductionTotalScore(
            student,
            level1Items,
            evalItemsData,
            excludeEmptyGrade
          );
        } else {
          // 기존 합산 방식
          totalScore = level1Items.reduce((sum, item) => {
            const rawScore = calculateLevel1Score(student, item, evalItemsData);

            // 유효한 숫자인 경우에만 totalScore에 더함
            if (rawScore && !isNaN(rawScore)) {
              return sum + Number(rawScore);
            }

            return sum;
          }, 0);
        }

        row["Total Score"] = totalScore > 0 ? totalScore : "";
        row["Grade"] = gradeToConvert100(
          totalScore,
          flexibleData?.find((r) => r.sub_category === "평가항목별")?.data_value
        );
      } else {
        // 평가항목별이 아닌 경우
        row["Total Score"] = student.grade || "";
        row["Grade"] = student.convert100 || "";
      }

      row["Incomplete or Withdrawn"] = student.extra_data?.academicStatus || "";

      return row;
    });

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, "Grades");
    XLSX.writeFile(wb, `${lessonInfo.subject_name}_grades.xlsx`);
  };

  const level1Items = evalItemsData.filter(
    (item) => item.lesson_id === lessonInfo.id && item.hierarchy_level === 1
  );

  // havenStyle 설정 확인 (테이블 렌더링용)
  const covert100Setting = flexibleData?.find(
    (r) => r.sub_category === "평가항목별" && r.main_category === "convert100"
  )?.data_value;
  const havenStyle = covert100Setting?.havenStyle || false;
  const excludeEmptyGrade = covert100Setting?.excludeEmptyGrade || false;

  return (
    <div className={styles.tableContainer}>
      <div className="d-flex">
        <h3>{t("teacher.lessonRelated.resultOfConversion")}</h3>
        <button
          className="btn btn-success btn-sm"
          onClick={handleExcelDownload}
        >
          {t("common.labels.excelConversion")}
        </button>
      </div>
      <table className={styles.gradeTable}>
        <thead>
          <tr>
            <th>Student Number</th>
            <th>Name</th>

            {lessonInfo.evaluation_type === "평가항목별" &&
              level1Items.map((item) => (
                <th key={item.id}>
                  {item.content} ({item?.extra_data?.weight}%)
                </th>
              ))}
            <th>Total Score</th>
            <th>Grade</th>
            <th>Incomplete or Withdrawn</th>
          </tr>
        </thead>
        <tbody>
          {lessonStudentsData.map((student) => {
            let totalScore = 0;

            if (lessonInfo.evaluation_type === "평가항목별") {
              // 평가항목별인 경우
              // havenStyle에 따라 총점 계산 방식 선택
              if (havenStyle) {
                // 감점 방식
                totalScore = calculateDeductionTotalScore(
                  student,
                  level1Items,
                  evalItemsData,
                  excludeEmptyGrade
                );
              } else {
                // 기존 합산 방식
                level1Items.forEach((item) => {
                  const rawScore = calculateLevel1Score(
                    student,
                    item,
                    evalItemsData
                  );
                  if (rawScore && !isNaN(rawScore)) {
                    totalScore += Number(rawScore);
                  }
                });
              }

              return (
                <tr key={student.id}>
                  <td>{student.username}</td>
                  <td>{student.fullname}</td>
                  {level1Items.map((item) => {
                    const rawScore = calculateLevel1Score(
                      student,
                      item,
                      evalItemsData
                    );
                    return <td key={item.id}>{rawScore}</td>;
                  })}
                  <td>{totalScore > 0 ? totalScore : ""}</td>
                  <td>
                    {gradeToConvert100(
                      totalScore,
                      flexibleData?.find((r) => r.sub_category === "평가항목별")
                        ?.data_value
                    )}
                  </td>
                  <td>
                    <select
                      value={student.extra_data?.academicStatus || "--------"}
                      onChange={(e) =>
                        saveStudentExtraData(student, e.target.value)
                      }
                    >
                      <option value="--------">--------</option>
                      <option value="incomplete">Incomplete</option>
                      <option value="withdrawn">Withdrawn</option>
                    </select>
                  </td>
                </tr>
              );
            } else {
              // 평가항목별이 아닌 경우
              totalScore = student.grade || "";
              return (
                <tr key={student.id}>
                  <td>{student.username}</td>
                  <td>{student.fullname}</td>
                  <td>{totalScore}</td>
                  <td>{student.convert100 || ""}</td>
                  <td>
                    <select
                      value={student.extra_data?.academicStatus || "--------"}
                      onChange={(e) =>
                        saveStudentExtraData(student, e.target.value)
                      }
                    >
                      <option value="--------">--------</option>
                      <option value="incomplete">Incomplete</option>
                      <option value="withdrawn">Withdrawn</option>
                    </select>
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    </div>
  );
}

export default HavenExtraTable;
